import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import StaffFansStatsCard from '../components/StaffFansStatsCard';
import Icon from '../components/Icon';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';
import DeleteUserModal from '../components/DeleteUserModal';
import Pagination from '../components/Pagination';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const USERS_PAGE_SIZE = 100;

const SORT_FIELD_MAP = {
  name: 'name',
  email: 'email',
  points: 'points',
  lastActivity: 'lastActivity',
  registrationDate: 'registrationDate',
  school: 'school',
  role: 'role',
};

function buildOrderingParam(sortConfig) {
  if (!sortConfig?.key) {
    return '-created_at';
  }

  const field = SORT_FIELD_MAP[sortConfig.key];
  if (!field) {
    return '-created_at';
  }

  return sortConfig.direction === 'desc' ? `-${field}` : field;
}

const SuperuserUserManagement = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStaff: 0,
    totalSuperusers: 0,
    totalSchools: 0,
    loading: true
  });

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [schools, setSchools] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserErrors, setCreateUserErrors] = useState({});
  const [createUserForm, setCreateUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    role: 'user',
    is_active: true,
    active_school: ''
  });
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserErrors, setEditUserErrors] = useState({});
  const [editUserId, setEditUserId] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user',
    is_active: true,
    active_school: '',
    city: '',
    state: '',
    phone_number: ''
  });
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [deleteUserErrors, setDeleteUserErrors] = useState({});
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deleteUserMasterKey, setDeleteUserMasterKey] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = {
        page,
        page_size: USERS_PAGE_SIZE,
        ordering: buildOrderingParam(sortConfig),
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const response = await apiService.getAllUsersSuperuser(params);
      const summary = response.summary || {};

      setUsers(response.users || []);
      setTotalCount(response.count ?? summary.total_users ?? 0);
      setStats({
        totalUsers: summary.total_users ?? 0,
        totalStaff: summary.staff_users ?? 0,
        totalSuperusers: summary.superusers ?? 0,
        totalSchools: summary.total_schools ?? 0,
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setStats(prev => ({ ...prev, loading: false }));
      navigate('/login/');
    } finally {
      setUsersLoading(false);
    }
  }, [page, debouncedSearch, sortConfig, navigate]);

  const initializePage = useCallback(async () => {
    try {
      const currentUser = await apiService.fetchCurrentUser();
      setCurrentUser(currentUser);

      if (!currentUser?.is_superuser) {
        navigate('/staff/dashboard/');
        return;
      }

      setIsAuthorized(true);

      try {
        const schoolsData = await apiService.getSchools();
        const schoolList = Array.isArray(schoolsData) ? schoolsData : (schoolsData.results || []);
        setSchools(schoolList.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (e) {
        setSchools([]);
      }
    } catch (error) {
      console.error('Failed to initialize user management:', error);
      setStats(prev => ({ ...prev, loading: false }));
      navigate('/login/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    initializePage();
  }, [initializePage]);

  useEffect(() => {
    if (!isAuthorized) {
      return;
    }
    fetchUsers();
  }, [isAuthorized, fetchUsers]);

  useEffect(() => {
    // Check for success message from navigation
    if (location.state?.message) {
      showToast(location.state.message, location.state.type || 'success');
      // Clear the state to prevent showing the toast again
      navigate(location.pathname, { replace: true, state: {} });
      // Refresh user data after adding points
      setTimeout(() => {
        fetchUsers();
      }, 500);
    }
  }, [location.state, navigate, location.pathname, fetchUsers]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setPage(1);
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getUserRole = (user) => {
    if (user.is_superuser) return 'Superuser';
    if (user.is_staff) return 'Staff';
    if (user.is_merchant_specialist) return 'Merchant Specialist';
    if (user.is_embed_tester) return 'Embed Tester';
    return 'User';
  };

  const handleMenuToggle = (userId) => {
    setOpenMenuUserId(openMenuUserId === userId ? null : userId);
  };

  const handleAddPoints = (user) => {
    navigate(`/superuser/add-points/${user.id}`, {
      state: {
        user: {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email
        }
      }
    });
    setOpenMenuUserId(null);
  };

  const canImpersonateEmbed = (user) => {
    return !!user?.is_active && !user?.is_staff && !user?.is_superuser && !user?.is_merchant_specialist;
  };

  const canDeleteUser = (user) => {
    if (!user || !currentUser) return false;
    if (user.id === currentUser.id) return false;
    if (user.is_superuser) return false;
    return true;
  };

  const openDeleteUser = (targetUser) => {
    if (!canDeleteUser(targetUser)) {
      showToast('This user cannot be deleted from this panel', 'error');
      return;
    }
    setDeleteUserErrors({});
    setDeleteUserTarget(targetUser);
    setDeleteUserMasterKey('');
    setIsDeleteUserOpen(true);
    setOpenMenuUserId(null);
  };

  const closeDeleteUser = () => {
    if (deleteUserLoading) return;
    setIsDeleteUserOpen(false);
    setDeleteUserErrors({});
    setDeleteUserTarget(null);
    setDeleteUserMasterKey('');
  };

  const handleDeleteUserMasterKeyChange = (e) => {
    setDeleteUserMasterKey(e.target.value);
    if (deleteUserErrors.master_key) {
      setDeleteUserErrors(prev => ({ ...prev, master_key: undefined }));
    }
    if (deleteUserErrors.detail) {
      setDeleteUserErrors(prev => ({ ...prev, detail: undefined }));
    }
    if (deleteUserErrors.error) {
      setDeleteUserErrors(prev => ({ ...prev, error: undefined }));
    }
  };

  const submitDeleteUser = async (e) => {
    e.preventDefault();
    if (deleteUserLoading || !deleteUserTarget) return;

    const masterKey = deleteUserMasterKey.trim();
    if (!masterKey) {
      setDeleteUserErrors({ master_key: ['Master key is required'] });
      return;
    }

    setDeleteUserLoading(true);
    setDeleteUserErrors({});
    try {
      const response = await apiService.deleteUserSuperuser(deleteUserTarget.id, masterKey);
      closeDeleteUser();
      showToast(response.message || 'User deleted successfully', 'success');
      await fetchUsers();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        setDeleteUserErrors(data);
      } else {
        showToast('Failed to delete user', 'error');
      }
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const handleImpersonateEmbed = async (user) => {
    setOpenMenuUserId(null);
    if (!canImpersonateEmbed(user)) {
      showToast('Only active non-staff users can be impersonated', 'error');
      return;
    }

    try {
      const response = await apiService.createEmbedImpersonationSuperuser(user.id, { view: 'home' });
      if (response?.impersonation_url) {
        window.open(response.impersonation_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('Failed to create impersonation link', 'error');
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to create impersonation link';
      showToast(message, 'error');
    }
  };

  const openEditUser = (targetUser) => {
    const role = targetUser.is_superuser
      ? 'superuser'
      : (targetUser.is_staff
        ? 'staff'
        : (targetUser.is_merchant_specialist
          ? 'merchant_specialist'
          : (targetUser.is_embed_tester ? 'embed_tester' : 'user')));
    setEditUserErrors({});
    setEditUserId(targetUser.id);
    setEditUserForm({
      first_name: targetUser.first_name || '',
      last_name: targetUser.last_name || '',
      email: targetUser.email || '',
      role,
      is_active: !!targetUser.is_active,
      active_school: targetUser.active_school?.id || '',
      city: targetUser.city || '',
      state: targetUser.state || '',
      phone_number: targetUser.phone_number || '',
    });
    setIsEditUserOpen(true);
  };

  const closeEditUser = () => {
    if (editUserLoading) return;
    setIsEditUserOpen(false);
    setEditUserErrors({});
    setEditUserId(null);
  };

  const handleEditUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (editUserErrors[name]) {
      setEditUserErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const submitEditUser = async (e) => {
    e.preventDefault();
    if (editUserLoading || !editUserId) return;

    const nextErrors = {};
    if (!editUserForm.first_name.trim()) nextErrors.first_name = 'First name is required';
    if (!editUserForm.last_name.trim()) nextErrors.last_name = 'Last name is required';
    if (!editUserForm.email.trim()) nextErrors.email = 'Email is required';
    if (Object.keys(nextErrors).length > 0) {
      setEditUserErrors(nextErrors);
      return;
    }

    const role = editUserForm.role;
    const payload = {
      email: editUserForm.email.trim().toLowerCase(),
      first_name: editUserForm.first_name.trim(),
      last_name: editUserForm.last_name.trim(),
      is_active: !!editUserForm.is_active,
      is_staff: role === 'staff' || role === 'superuser',
      is_superuser: role === 'superuser',
      is_embed_tester: role === 'embed_tester',
      is_merchant_specialist: role === 'merchant_specialist',
      active_school: role === 'merchant_specialist'
        ? null
        : (editUserForm.active_school ? editUserForm.active_school : null),
      city: (editUserForm.city || '').trim(),
      state: (editUserForm.state || '').trim(),
      phone_number: (editUserForm.phone_number || '').trim(),
    };

    setEditUserLoading(true);
    setEditUserErrors({});
    try {
      await apiService.updateUserSuperuser(editUserId, payload);
      setIsEditUserOpen(false);
      showToast('User updated successfully', 'success');
      await fetchUsers();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        setEditUserErrors(data);
      } else {
        showToast('Failed to update user', 'error');
      }
    } finally {
      setEditUserLoading(false);
    }
  };

  const openCreateUser = () => {
    setCreateUserErrors({});
    setCreateUserForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirm: '',
      role: 'user',
      is_active: true,
      active_school: ''
    });
    setIsCreateUserOpen(true);
  };

  const closeCreateUser = () => {
    if (createUserLoading) return;
    setIsCreateUserOpen(false);
    setCreateUserErrors({});
  };

  const handleCreateUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (createUserErrors[name]) {
      setCreateUserErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (name === 'password' || name === 'password_confirm') {
      if (createUserErrors.password_confirm) {
        setCreateUserErrors(prev => ({ ...prev, password_confirm: undefined }));
      }
    }
  };

  const submitCreateUser = async (e) => {
    e.preventDefault();
    if (createUserLoading) return;

    const nextErrors = {};
    if (!createUserForm.first_name.trim()) nextErrors.first_name = 'First name is required';
    if (!createUserForm.last_name.trim()) nextErrors.last_name = 'Last name is required';
    if (!createUserForm.email.trim()) nextErrors.email = 'Email is required';
    if (!createUserForm.password) nextErrors.password = 'Password is required';
    if (createUserForm.password !== createUserForm.password_confirm) nextErrors.password_confirm = 'Passwords do not match';

    if (Object.keys(nextErrors).length > 0) {
      setCreateUserErrors(nextErrors);
      return;
    }

    const role = createUserForm.role;
    const payload = {
      email: createUserForm.email.trim().toLowerCase(),
      first_name: createUserForm.first_name.trim(),
      last_name: createUserForm.last_name.trim(),
      password: createUserForm.password,
      is_active: !!createUserForm.is_active,
      is_staff: role === 'staff' || role === 'superuser',
      is_superuser: role === 'superuser',
      is_embed_tester: role === 'embed_tester',
      is_merchant_specialist: role === 'merchant_specialist',
      active_school: role === 'merchant_specialist'
        ? null
        : (createUserForm.active_school ? createUserForm.active_school : null),
    };

    setCreateUserLoading(true);
    setCreateUserErrors({});
    try {
      await apiService.createUserSuperuser(payload);
      setIsCreateUserOpen(false);
      showToast('User created successfully', 'success');
      await fetchUsers();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        setCreateUserErrors(data);
      } else {
        showToast('Failed to create user', 'error');
      }
    } finally {
      setCreateUserLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />

          <h1 className="staff-page-header">Manage Users</h1>
          <p className="staff-page-subheader">Manage all users across the platform</p>

          {/* Stats Cards */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Total Users"
                value={stats.loading ? "..." : stats.totalUsers}
                iconName="people"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Staff Members"
                value={stats.loading ? "..." : stats.totalStaff}
                iconName="badge"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Superusers"
                value={stats.loading ? "..." : stats.totalSuperusers}
                iconName="star"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Active Schools"
                value={stats.loading ? "..." : stats.totalSchools}
                iconName="home"
              />
            </div>
          </div>

          {/* Search Bar */}
          <div style={{
            marginTop: '32px',
            marginBottom: '24px',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1 1 380px', minWidth: '260px' }}>
              <input
                type="text"
                placeholder="Search users . . ."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: '"Baikal Book"'
                }}
              />
            </div>
            <button
              onClick={openCreateUser}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #26CDFF',
                backgroundColor: '#26CDFF',
                color: 'white',
                cursor: 'pointer',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              <Icon name="plus" size={14} />
              Create User
            </button>
          </div>

          {/* Table */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '1270px',
            marginTop: '24px'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              width: '100%',
              height: '45px',
              padding: '0 18px',
              alignItems: 'center',
              borderRadius: '12px 12px 0 0',
              background: '#F8F8F8',
              backdropFilter: 'blur(22.5px)'
            }}>
              <div
                onClick={() => handleSort('name')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '18%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                Name
                <Icon name="up-down" size={12} />
              </div>
              <div
                onClick={() => handleSort('email')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '22%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                Email
                <Icon name="up-down" size={12} />
              </div>
              <div
                onClick={() => handleSort('school')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '18%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                School
                <Icon name="up-down" size={12} />
              </div>
              <div
                onClick={() => handleSort('role')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '12%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                Role
                <Icon name="up-down" size={12} />
              </div>
              <div
                onClick={() => handleSort('points')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '12%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                Points
                <Icon name="up-down" size={12} />
              </div>
              <div
                onClick={() => handleSort('registrationDate')}
                style={{
                  fontFamily: '"Baikal Book"',
                  fontWeight: '900',
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '16%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  paddingRight: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                Registration Date
                <Icon name="up-down" size={12} />
              </div>
              <div style={{
                width: '5%',
                minWidth: '40px',
                flexShrink: 0,
                textAlign: 'center'
              }}>
                {/* Actions column header */}
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}></div>

            {!usersLoading && (
              <Pagination
                position="top"
                page={page}
                totalItems={totalCount}
                pageSize={USERS_PAGE_SIZE}
                onPageChange={setPage}

              />
            )}

            {/* Table Rows */}
            {usersLoading ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666'
              }}>
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666'
              }}>
                No users found.
              </div>
            ) : (
              users.map((user, index) => (
                <div key={user.id || index} style={{
                  display: 'flex',
                  width: '100%',
                  height: '59px',
                  padding: '18px',
                  alignItems: 'center',
                  borderRadius: '12px',
                  background: '#F8F8F8',
                  marginTop: '8px',
                  marginBottom: index === users.length - 1 ? '8px' : '0',
                }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '18%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {`${user.first_name} ${user.last_name}`}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '22%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px',
                    opacity: 0.7
                  }}>
                    {user.email}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '18%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px',
                    paddingLeft: user.active_school?.image ? '12px' : '0px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {user.active_school?.image && (
                      <img
                        src={user.active_school.image}
                        alt={`${user.active_school.name} logo`}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '4px',
                          objectFit: 'contain',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.active_school?.image ? '' : (user.active_school?.name || 'No School')}
                    </span>
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '12%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px',
                    fontWeight: user.is_superuser ? 'bold' : 'normal',
                    color: user.is_superuser ? '#dc2626' : (user.is_staff ? '#2563eb' : (user.is_merchant_specialist ? '#0f766e' : (user.is_embed_tester ? '#7c3aed' : 'inherit')))
                  }}>
                    {getUserRole(user)}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '12%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {(user.lifetime_points || 0).toLocaleString()}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '16%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {formatDate(user.created_at || user.date_joined)}
                  </div>
                  <div style={{
                    width: '5%',
                    minWidth: '40px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    position: 'relative',
                    paddingLeft: '0px'
                  }}>
                    <button
                      onClick={() => handleMenuToggle(user.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '12px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#374151',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="more-vertical" size={24} style={{ color: '#000000' }} />
                      ⋮
                    </button>
                    {openMenuUserId === user.id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '140px'
                      }}>
                        <button
                          onClick={() => handleAddPoints(user)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontFamily: '"Baikal Book"',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Icon name="plus" size={14} />
                          Add Points
                        </button>
                        {canImpersonateEmbed(user) && (
                          <button
                            onClick={() => handleImpersonateEmbed(user)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: '"Baikal Book"',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Icon name="play" size={14} />
                            Impersonate embed
                          </button>
                        )}
                        <button
                          onClick={() => {
                            openEditUser(user);
                            setOpenMenuUserId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontFamily: '"Baikal Book"',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Icon name="settings" size={14} />
                          Edit User
                        </button>
                        {canDeleteUser(user) && (
                          <button
                            onClick={() => openDeleteUser(user)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: '"Baikal Book"',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: '#dc2626'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#fef2f2';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            Delete User
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {!usersLoading && (
            <Pagination
              page={page}
              totalItems={totalCount}
              pageSize={USERS_PAGE_SIZE}
              onPageChange={setPage}
            />
          )}

          {/* Toast notification */}
          {toast && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              backgroundColor: toast.type === 'error' ? '#EF4444' : '#10B981',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1001,
              fontSize: '0.875rem',
              fontWeight: '500',
              maxWidth: '400px'
            }}>
              {toast.message}
              <button
                onClick={() => setToast(null)}
                style={{
                  marginLeft: '12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          )}
          <CreateUserModal
            isOpen={isCreateUserOpen}
            onClose={closeCreateUser}
            loading={createUserLoading}
            errors={createUserErrors}
            form={createUserForm}
            onChange={handleCreateUserChange}
            onSubmit={submitCreateUser}
            schools={schools}
          />

          <EditUserModal
            isOpen={isEditUserOpen}
            onClose={closeEditUser}
            loading={editUserLoading}
            errors={editUserErrors}
            form={editUserForm}
            onChange={handleEditUserChange}
            onSubmit={submitEditUser}
            schools={schools}
          />

          <DeleteUserModal
            isOpen={isDeleteUserOpen}
            onClose={closeDeleteUser}
            loading={deleteUserLoading}
            errors={deleteUserErrors}
            targetUser={deleteUserTarget}
            masterKey={deleteUserMasterKey}
            onMasterKeyChange={handleDeleteUserMasterKeyChange}
            onSubmit={submitDeleteUser}
          />
        </div>
      </div>
    </div>
  );
};

export default SuperuserUserManagement;